import React, { Component } from 'react';
import { bool, string } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Field, Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';
import { isUploadImageOverLimitError } from '../../util/errors';
import {
  Form,
  Avatar,
  Button,
  ImageFromFile,
  IconSpinner,
  FieldTextInput,
  FieldAddress,
  FieldWorkerExperience,
  FieldPermitAndCertificate,
  FieldReference,
  FieldLanguagesKnown,
  NamedLink,
} from '../../components';
import arrayMutators from 'final-form-arrays';

import css from './ProfileSettingsForm.module.css';
import { deleteProfileImage } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';

const ACCEPT_IMAGES = 'image/*';
const UPLOAD_CHANGE_DELAY = 2000; // Show spinner so that browser has time to load img srcset

class WorkerQualificationFormComponent extends Component {
  constructor(props) {
    super(props);

    this.uploadDelayTimeoutId = null;
    this.state = { uploadDelay: false };
    this.submittedValues = {};
  }

  componentDidUpdate(prevProps) {
    // Upload delay is additional time window where Avatar is added to the DOM,
    // but not yet visible (time to load image URL from srcset)
    if (prevProps.uploadInProgress && !this.props.uploadInProgress) {
      this.setState({ uploadDelay: true });
      this.uploadDelayTimeoutId = window.setTimeout(() => {
        this.setState({ uploadDelay: false });
      }, UPLOAD_CHANGE_DELAY);
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.uploadDelayTimeoutId);
  }

  render() {
    return (
      <FinalForm
        {...this.props}
        mutators={{ ...arrayMutators }}
        render={fieldRenderProps => {
          const {
            className,
            currentUser,
            handleSubmit,
            intl,
            invalid,
            onImageUpload,
            pristine,
            profileImage,
            rootClassName,
            updateInProgress,
            updateProfileError,
            uploadImageError,
            uploadInProgress,
            form,
            values,
            // onUpdateProfile,
            customUpdateProfile,
            initialValues,
            userNativeLang,
            onManageDisableScrolling,
            error,
          } = fieldRenderProps;

          const user = ensureCurrentUser(currentUser);
          const textfield = 'textfield';
          const errorTextField = 'errorTextField';

          // Company name
          const companyNameLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.companyNameLabel',
          });
          const companyNamePlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.companyNamePlaceholder',
          });
          const companyNameRequiredMessage = intl.formatMessage({
            id: 'ProfileSettingsForm.companyNameRequired',
          });
          const companyNameRequired = validators.required(companyNameRequiredMessage);

          // Industry
          const industryLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.industryLabel',
          });
          const industryPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.industryPlaceholder',
          });
          const industryRequiredMessage = intl.formatMessage({
            id: 'ProfileSettingsForm.industryRequired',
          });
          const industryRequired = validators.required(industryRequiredMessage);

          // business address
          const addressLabel = intl.formatMessage({
            id: 'SignupForm.adrressLabel',
          });
          const addressPlaceholder = intl.formatMessage({
            id: 'SignupForm.adrressPlaceholder',
          });
          const postalLabel = intl.formatMessage({
            id: 'SignupForm.postalLabel',
          });
          const postalPlaceholder = intl.formatMessage({
            id: 'SignupForm.postalPlaceholder',
          });
          const addressRequiredMessage = intl.formatMessage({
            id: 'SignupForm.adrressRequired',
          });
          const addressRequired = validators.addressRequired(addressRequiredMessage);

          // Last name
          // const lastNameLabel = intl.formatMessage({
          //   id: 'ProfileSettingsForm.lastNameLabel',
          // });
          // const lastNamePlaceholder = intl.formatMessage({
          //   id: 'ProfileSettingsForm.lastNamePlaceholder',
          // });
          // const lastNameRequiredMessage = intl.formatMessage({
          //   id: 'ProfileSettingsForm.lastNameRequired',
          // });
          // const lastNameRequired = validators.required(lastNameRequiredMessage);

          // companyDesc
          const companyDescLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.companyDescLabel',
          });
          const companyDescPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.companyDescPlaceholder',
          });

          const uploadingOverlay =
            uploadInProgress || this.state.uploadDelay ? (
              <div className={css.uploadingImageOverlay}>
                <IconSpinner />
              </div>
            ) : null;

          const hasUploadError = !!uploadImageError && !uploadInProgress;
          const errorClasses = classNames({
            [css.avatarUploadError]: hasUploadError,
          });
          //   const transientUserProfileImage = profileImage.uploadedImage || user.profileImage;
          //   const transientUser = {
          //     ...user,
          //     profileImage: transientUserProfileImage,
          //   };

          // Ensure that file exists if imageFromFile is used

          const submitError = updateProfileError ? (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
            </div>
          ) : null;

          const classes = classNames(rootClassName || css.root, className);
          const submitInProgress = updateInProgress;
          const submittedOnce = Object.keys(this.submittedValues).length > 0;
          const pristineSinceLastSubmit = submittedOnce && isEqual(values, this.submittedValues);
          const sum = values?.references?.filter(f => f.isEditMode);

          const submitDisabled =
            invalid ||
            pristine ||
            pristineSinceLastSubmit ||
            uploadInProgress ||
            submitInProgress ||
            sum?.length > 0;

          return (
            <Form
              className={classes}
              onSubmit={e => {
                this.submittedValues = values;
                handleSubmit(e);
              }}
            >
              <div className={css.sectionContainer}>
                <FieldWorkerExperience
                  id={'jobExperiences'}
                  formApi={form}
                  name="jobExperiences"
                  userNativeLang={userNativeLang}
                  onManageDisableScrolling={onManageDisableScrolling}
                />
              </div>
              <div className={css.sectionContainer}>
                <FieldPermitAndCertificate
                  id={'permitsAndCertificates'}
                  formApi={form}
                  userNativeLang={userNativeLang}
                  name="permitsAndCertificates"
                  formError={error}
                />
              </div>
              <div className={css.sectionContainer}>
                <FieldLanguagesKnown
                  formApi={form}
                  id={'knownLanguages'}
                  name="knownLanguages"
                  userNativeLang={userNativeLang}
                  titleClassName={css.workerJobCategoryTitle}
                  values={values}
                  isRequired={true}
                  intl={intl}
                />
                {/* <FieldLanguagesKnown
              id={'languageRequirements'}
              name="languageRequirements"
             
              
            /> */}
              </div>
              <div className={css.sectionContainer}>
                <FieldReference
                  id={'references'}
                  formApi={form}
                  userNativeLang={userNativeLang}
                  name="references"
                  formError={error}
                  user={user}
                  fromWorkerQualification={true}
                />
              </div>

              {submitError}

              <div className={css.btncomp}>
                <div className={css.submitcomp}>
                  <Button
                    className={css.submitButton}
                    type="submit"
                    inProgress={submitInProgress}
                    disabled={submitDisabled}
                    ready={pristineSinceLastSubmit}
                  >
                    <FormattedMessage id="ProfileSettingsForm.saveChanges" />
                  </Button>
                </div>

                <NamedLink name="WorkerQualificationPage" className={css.cancelbtn}>
                  <FormattedMessage id="ProfileSettingsForm.Cancel" />
                </NamedLink>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}
WorkerQualificationFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  uploadImageError: null,
  updateProfileError: null,
  updateProfileReady: false,
};

WorkerQualificationFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  uploadImageError: propTypes.error,
  uploadInProgress: bool.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  updateProfileReady: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const WorkerQualificationForm = compose(injectIntl)(WorkerQualificationFormComponent);

WorkerQualificationForm.displayName = 'WorkerQualificationForm';

export default WorkerQualificationForm;
